/* eslint-disable max-len */
import React, {
  useLayoutEffect, useState, useRef, useContext, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ChevronLeft } from 'lucide-react';
import styled from '@emotion/styled';
import HomeIcon from '../../images/icon-house.svg';
import AlertIcon from '../../images/icon-alert.svg';
import { formatTime } from '../../utils/time';
import { LocationViolationType, UserType } from '../../propTypes';
import { sendRequest } from '../../api/shootAPI';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import API from '../../api/endpoints';
import { ADDRESSS_HOME, ADDRESS_AWAY } from '../../constants/geolocation';
import ResolveModal from './ResolveModal';
import './locationHistory.css';

const SidebarWrapper = styled.div`
  width: 288px;
  height: 100vh;
  border-right: 1px solid #e0e1e6;
  font-family: Inter, sans-serif;
  position: relative;
  background: #fff;
`;

const StyledLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #3d5a8a;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const PersonalInfo = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e0e1e6;
  background: #fbfbfc;
  display: flex;
  cursor: pointer;
`;

export const IconWrapper = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border: 0.75px solid rgba(255, 255, 255, 1);
  box-shadow: 0px 0px 1.5px 0px rgba(0, 0, 0, 0.25);
  background: ${(props) => props.backgroundColor};
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const HistoryWrapper = styled.div`
  display: flex;
  cursor: pointer;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f2f5;
  transition: background 0.12s ease;

  &:hover {
    background: #fbfbfc;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid #e0e1e6;
  background: #fff;
  padding: 6px 12px;
  color: #0e1e40;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.12s ease, color 0.12s ease;

  &:hover {
    border-color: #53A551;
    color: #53A551;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BottomButtonWrapper = styled.div`
  padding: 16px 20px;
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  width: 288px;
  background: #fff;
  border-top: 1px solid #e0e1e6;
`;

// eslint-disable-next-line max-len
const resolveLocationViolation = async (locationViolationIds, workspaceId, setResolving, onSuccess, displayNotification, isAll) => {
  setResolving(true);
  const response = await sendRequest({
    method: 'put',
    url: API.WORKSPACE.resolveLocationViolation(workspaceId),
    data: { locationViolationIds },
  });
  setResolving(false);
  if (response.success) {
    displayNotification({ message: isAll ? 'All issues resolved.' : 'Issue resolved.', severity: 'success' });
    onSuccess(true);
  } else {
    displayNotification({ message: 'There was an issue resolving. Please try again.', severity: 'error' });
  }
};

function HistoryItem({
  locationViolation, clickLocation, onResolve, selected,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [isResolving, setIsResolving] = useState(false);

  return (
    <HistoryWrapper
      onClick={() => clickLocation(locationViolation, ADDRESS_AWAY)}
      className={selected && 'selectedLocation'}
      data-testid="violation-history"
    >
      <IconWrapper
        width="20px"
        height="20px"
        backgroundColor="var(--Red-Red-02, #CC4525)"
        data-testid="alert-icon"
      >
        <img src={AlertIcon} alt="alert icon" />
      </IconWrapper>
      <Box sx={{ marginLeft: '6px' }}>
        <Typography
          sx={{
            color: '#b94f3c',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '20px',
          }}
          data-testid="away-from-home"
        >
          Away from home
        </Typography>
        <Typography
          sx={{
            color: '#546077',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px',
            marginTop: '4px',
          }}
          data-testid="violation-startTime"
        >
          {formatTime(locationViolation.createdAt)}
        </Typography>
        <Typography
          sx={{
            color: '#546077',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px',
            marginTop: '2px',
          }}
          data-testid="violation-duration"
        >
          {`${locationViolation.activityIds.length * 10} mins`}
        </Typography>
        <Typography
          sx={{
            color: '#0e1e40',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '18px',
            marginTop: '8px',
          }}
          data-testid="violation-address"
        >
          {locationViolation.formattedAddress}
        </Typography>
        <Button
          disabled={isResolving}
          style={{
            marginTop: '16px',
          }}
          onClick={(e) => {
            setIsModalOpen(true);
            e.stopPropagation();
          }}
          data-testid="resolve"
        >
          {isResolving ? 'Resolving' : 'Resolve'}
        </Button>
      </Box>
      {isModalOpen && (
        <ResolveModal
          closeModal={() => setIsModalOpen(false)}
          handleResolve={() => resolveLocationViolation([locationViolation.id], activeWorkspace.id, setIsResolving, onResolve, displayNotification, false)}
          isAll={false}
        />
      )}
    </HistoryWrapper>
  );
}

HistoryItem.propTypes = {
  locationViolation: LocationViolationType,
  clickLocation: PropTypes.func,
  onResolve: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
};

function Sidebar({
  user, locationViolations, handleClose, clickLocation, onResolve, selectedLocation,
}) {
  const { formattedAddress } = user.userLocation;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyWrapperPos, setHistoryWrapperPos] = useState({ top: 0, left: 0 });
  const [isResolving, setIsResolving] = useState(false);
  const locationViolationIds = useMemo(() => (
    locationViolations
      ? locationViolations.map((locationViolation) => locationViolation.id) : []
  ), [locationViolations]);
  const divRef = useRef(null);
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  useLayoutEffect(() => {
    if (divRef.current) {
      const rect = divRef.current.getBoundingClientRect();
      setHistoryWrapperPos({
        top: rect.top,
        left: rect.left,
      });
    }
  }, []);

  return (
    <SidebarWrapper>
      <Box sx={{ padding: '16px 20px' }}>
        <StyledLink
          className="back-link"
          onClick={() => handleClose()}
          data-testid="back-link"
        >
          <ChevronLeft size={16} />
          Geo Dashboard
        </StyledLink>
      </Box>
      <PersonalInfo
        onClick={() => clickLocation(user.userLocation, ADDRESSS_HOME)}
        className={selectedLocation === -1 && 'selectedLocation'}
      >
        <IconWrapper
          width="30px"
          height="30px"
          backgroundColor="var(--Green-Green-04, #64BA62)"
          data-testid="home-icon"
        >
          <img src={HomeIcon} alt="home" />
        </IconWrapper>
        <Box sx={{ marginLeft: '15px' }}>
          <Typography
            sx={{
              color: '#0e1e40',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '18px',
              letterSpacing: 0,
              textAlign: 'left',
              marginBottom: '6px',
            }}
            data-testid="name"
          >
            {`${user.firstName} ${user.lastName}`}
          </Typography>
          <Typography
            sx={{
              color: '#546077',
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: '14px',
              letterSpacing: 0,
              textTransform: 'uppercase',
              textAlign: 'left',
              marginBottom: '2px',
            }}
            data-testid="home-address-label"
          >
            Home Address
          </Typography>
          <Typography
            sx={{
              color: '#3d4759',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              letterSpacing: 0,
              textAlign: 'left',
            }}
            data-testid="address"
          >
            {formattedAddress}
          </Typography>
        </Box>
      </PersonalInfo>
      <Box>
        <Typography sx={{
          color: '#0e1e40',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '20px',
          padding: '16px 20px',
        }}
        >
          {`Location History (${locationViolations.length})`}
        </Typography>
        {
          !locationViolations.length
          && (
            <Typography sx={{ textAlign: 'center', padding: '10px' }} data-testid="no-violation">
              No location violations.
            </Typography>
          )
        }
        {
          !!locationViolations.length
          && (
          <Box
            sx={{
              height: `calc(100vh - ${historyWrapperPos.top}px - 62px)`,
              overflowY: 'auto',
            }}
            ref={divRef}
          >
            {
            locationViolations.map((v) => (
              <HistoryItem
                key={v.id}
                locationViolation={v}
                clickLocation={clickLocation}
                onResolve={onResolve}
                selected={v.id === selectedLocation}
              />
            ))
          }
          </Box>
          )
        }
        <BottomButtonWrapper>
          <Button
            disabled={!locationViolations.length || isResolving}
            onClick={() => {
              setIsModalOpen(true);
            }}
            data-testid="resolve-all"
            style={{ width: '100%' }}
          >
            {isResolving ? 'Resolving' : 'Resolve All'}
          </Button>
        </BottomButtonWrapper>
      </Box>
      {isModalOpen && (
        <ResolveModal
          closeModal={() => setIsModalOpen(false)}
          handleResolve={() => resolveLocationViolation(locationViolationIds, activeWorkspace.id, setIsResolving, onResolve, displayNotification, true)}
          isAll
        />
      )}
    </SidebarWrapper>
  );
}

Sidebar.propTypes = {
  user: UserType,
  locationViolations: PropTypes.arrayOf(LocationViolationType),
  handleClose: PropTypes.func.isRequired,
  clickLocation: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  selectedLocation: PropTypes.number.isRequired,
};

export default Sidebar;
